// src/pages/quality/CarNew.jsx
import React, { useMemo, useState, useCallback, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import FormContentManager from "../../../components/common/form/FormContentManager";
import { getQualityMetaList, getQualityMeta } from "../../../api/quality/meta";
import TableCell from "../../../components/common/ui/TableCell";
import PeopleCellLoader from "../../../components/common/people/PeopleCellLoader";


// meta.form 혹은 평면 meta 대응
const pickFormFromMeta = (meta) => {
  if (!meta || typeof meta !== "object") return {};
  return meta.form || meta;
};

// NCR 상세 메타 (NcrNew에서 넣는 ncrMeta)
const pickNcrMeta = (meta) => {
  if (!meta || typeof meta !== "object") return {};
  return meta.ncrMeta || {};
};

export default function CarNew() {
  const { id: routeProjectId, dns: doc_dns } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [dns, setDns] = useState(doc_dns || "");


  const projectId =
    routeProjectId ||
    localStorage.getItem("projectId") ||
    localStorage.getItem("projectCode") ||
    "";

  const [linkedNcr, setLinkedNcr] = useState(null);
  const [ncrList, setNcrList] = useState([]);
  const [requestPeople, setRequestPeople] = useState([]);

  const [form, setForm] = useState({
    doc_date: new Date().toISOString().slice(0, 10),
    status: "draft",
    title: "",
    note: "",
  });

  // 시정조치 메타 (더미 예시 포함)
  const [carMeta, setCarMeta] = useState({
    ncr_dns: "",
    responsible: "",
    due_date: "",
    close_date: "",
    root_cause:
      "예시) 기준 미숙지 및 작업자 교육 미흡으로 인해 철근 피복 두께 미달 발생.",
    corrective_action:
      "예시) 해당 부위 철근 재시공 및 전수 점검을 실시하고, 부적합 물량 전량 폐기.",
    preventive_action:
      "예시) 작업 전 시공 기준 재교육 실시, 체크리스트 도입, 중간 점검 강화.",
    verification:
      "예시) 재시공 구간에 대해 피복 두께 실측 및 사진 기록 첨부, 기준 만족 여부 확인.",
    severity: "", // High / Medium / Low 선택
  });

  const onChange = (k, v) =>
    setForm((p) => ({
      ...p,
      [k]: v,
    }));

  const onChangeMeta = (k, v) =>
    setCarMeta((p) => ({
      ...p,
      [k]: v,
    }));

  // 선택된 NCR 기반 자동 제목
  const autoTitle = useMemo(() => {
    const meta = linkedNcr?.meta || {};
    const f = pickFormFromMeta(meta);

    const base = linkedNcr?.doc?.title?.trim() || f.title?.trim();
    if (base) return `[CAR] ${base}`;

    if (carMeta.ncr_dns) return `[CAR] ${carMeta.ncr_dns} 시정조치`;

    return "";
  }, [linkedNcr, carMeta.ncr_dns]);

  // NCR 한 건 로드 (DNS → 백엔드 detail)
  const loadNcr = useCallback(async (ncrDns) => {
    if (!ncrDns) return;
    try {
      const res = await getQualityMeta({ dns: ncrDns });
      const ncr = res?.data || res;

      setLinkedNcr(ncr);

      const meta = ncr?.meta || {};
      const f = pickFormFromMeta(meta);
      const nm = pickNcrMeta(meta);

      // 연결된 NCR DNS는 내부에서만 세팅 (사용자 직접 수정 X)
      setCarMeta((prev) => ({
        ...prev,
        ncr_dns: ncr.doc_dns || ncrDns,
        responsible: prev.responsible || nm.action_dept || "",
        due_date:
          prev.due_date || (nm.due_type === "date" ? nm.due_date : "") || "",
      }));

      // 제목 자동
      setForm((prev) => ({
        ...prev,
        title: prev.title?.trim()
          ? prev.title
          : ncr.doc?.title
            ? `[CAR] ${ncr.doc.title}`
            : f.title
              ? `[CAR] ${f.title}`
              : prev.title,
      }));
    } catch (err) {
      console.error("NCR 불러오기 실패:", err);
    }
  }, []);

  // URL ?fromNCR=... 있으면 자동 연결
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ncrDns = params.get("fromNCR");
    if (ncrDns) {
      loadNcr(ncrDns);
    }
  }, [location.search, loadNcr]);

  // 프로젝트 NCR 목록: __client__.form_type === "NC" 만
  useEffect(() => {
    const fetchNcrList = async () => {
      try {
        const res = await getQualityMetaList({
          project: projectId,
        });
        const rows = Array.isArray(res) ? res : res?.results || [];

        const onlyNcr = rows.filter((r) => {
          const meta = r.meta || {};
          const ft = String(
            meta.__client__?.form_type || meta.form_type || ""
          ).toUpperCase();
          return ft === "NC" || ft === "NCR";
        });

        setNcrList(onlyNcr);
      } catch (err) {
        console.error("NCR 목록 불러오기 실패:", err);
      }
    };

    if (projectId) fetchNcrList();
  }, [projectId]);

  const BOARD = `/project/${projectId}/quality/qualityboard`;

  useEffect(() => {
    if (!doc_dns) return; // 신규 생성은 스킵

    async function loadDetail() {
      try {
        const res = await getQualityMeta({ dns: doc_dns });
        console.log(res)
        const meta = res?.meta || {};

        // form
        if (meta.form) {
          setForm(prev => ({
            ...prev,
            ...meta.form,
          }));
        }

        // carMeta
        if (meta.carMeta) {
          setCarMeta(prev => ({
            ...prev,
            ...meta.carMeta,
          }));
        }

        // 링크된 NCR이 있었다면 자동 로드
        const linked = meta.carMeta?.ncr_dns;
        if (linked) {
          loadNcr(linked);
        }

      } catch (err) {
        console.error("CAR 수정모드 meta 로딩 실패:", err);
      }
    }

    loadDetail();
  }, [doc_dns]);


  // 저장 payload
  const getBody = useCallback(
    () => ({
      layout: null,
      values: {
        project: projectId,
        module: "quality",
        doc_type: "CAR",     // CAR 문서 유형
        dns,

        // ⭐ 핵심: 모든 CAR 데이터를 meta 로 감싸야 저장됨
        meta: {
          form: {
            ...form,
            title: form.title?.trim() || autoTitle,
          },
          carMeta,    // CAR 상세 메타 저장
          __client__: {
            form_type: "CA",   // 품질 form_type
          },
          related_dns: linkedNcr?.doc_dns || carMeta.ncr_dns || null,
        },
      },
    }),
    [projectId, dns, form, carMeta, autoTitle, linkedNcr]
  );

  // ───── NCR 요약 표시용 파생 데이터 ─────
  const ncrMeta = linkedNcr?.meta || {};
  const ncrForm = pickFormFromMeta(ncrMeta);
  const ncrDetail = pickNcrMeta(ncrMeta);

  const ncrTitle = linkedNcr?.doc?.title || ncrForm.title || "";
  const ncrResult =
    ncrForm.result === "pass"
      ? "적합"
      : ncrForm.result === "fail"
        ? "부적합"
        : "";
  const ncrReporter = ncrForm.proposer || "";
  const ncrAssignee = ncrDetail.action_dept || "";
  const ncrArea = ncrDetail.location || "";
  const ncrDue =
    ncrDetail.due_type === "date"
      ? ncrDetail.due_date || ""
      : ncrDetail.due_type === "immediate"
        ? "즉시"
        : "";
  const ncrCause = ncrDetail.description || "";
  const ncrAction = ncrDetail.action_plan || "";

  return (
    <FormContentManager
      params={{
        module: "quality",
        formType: "CA",
        mode: 3,
        scope: "project",
        project: projectId,
        dns,
        related_dns: linkedNcr?.doc_dns || carMeta.ncr_dns || null,
      }}
      getBody={getBody}
      onChangeId={(newDns) => {
        setDns(newDns);
        if (newDns) {
          navigate(
            `/project/${projectId}/quality/view/car/${encodeURIComponent(
              newDns
            )}`
          );
        }
      }}
      redirectTo={BOARD}
      docName="시정조치서 (CAR)"
      docDescribe="선택한 NCR에 대한 근본원인, 시정·예방조치, 검증 및 종결 내용을 기록합니다."
      requestPeople={requestPeople}
      summaryConfig={{
        title: "시정조치 요약",
        sections: [
          {
            label: "제목",
            value: form.title?.trim() || autoTitle || "-",
          },
          {
            label: "상태",
            value: form.status || "-",
          },
          {
            label: "문서일자",
            value: form.doc_date || "-",
          },
          {
            label: "연결 NCR",
            value: carMeta.ncr_dns || linkedNcr?.doc_dns || "-",
          },
        ],
        blocks: [
          {
            label: "근본원인",
            content: carMeta.root_cause?.trim() || "입력된 근본원인 없음",
          },
          {
            label: "시정조치",
            content:
              carMeta.corrective_action?.trim() || "입력된 시정조치 없음",
          },
          {
            label: "예방조치",
            content:
              carMeta.preventive_action?.trim() || "입력된 예방조치 없음",
          },
          {
            label: "검증",
            content: carMeta.verification?.trim() || "검증 내용 미입력",
          },
        ],
        list: [
          {
            label: "핵심 정보",
            items: [
              `책임부서/담당: ${carMeta.responsible || "-"}`,
              `조치기한: ${carMeta.due_date || "-"}`,
              `종결일: ${carMeta.close_date || "-"}`,
              `위험도: ${carMeta.severity || "-"}`,
            ],
          },
        ],
        footerNote: "더미 예시는 실제 상황에 맞게 수정해서 사용하세요.",
      }}
    >
      <div className="">
        {/* 상단: NCR 연결 */}
        <section className="rounded border bg-white p-4">
          <div className="flex items-center gap-2">
            <label className="font-medium text-sm">연결된 NCR</label>
            {linkedNcr ? (
              <span className="text-sm">{linkedNcr.doc_dns}</span>
            ) : (
              <select
                className="border rounded px-2 py-1 text-sm"
                onChange={(e) => e.target.value && loadNcr(e.target.value)}
                defaultValue=""
              >
                <option value="" disabled>
                  NCR 선택
                </option>
                {ncrList.map((ncr) => {
                  const m = ncr.meta || {};
                  const f = pickFormFromMeta(m);
                  return (
                    <option key={ncr.doc_dns} value={ncr.doc_dns}>
                      {ncr.doc_dns} - {f.title || ""}
                    </option>
                  );
                })}
              </select>
            )}
          </div>

          {/* NCR 요약 */}
          {linkedNcr && (
            <div className="mt-4 text-sm">
              <div className="font-semibold mb-2">연결된 NCR 요약</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
                <div>
                  <span className="text-gray-500">제목: </span>
                  {ncrTitle || "-"}
                </div>
                <div>
                  <span className="text-gray-500">결과: </span>
                  {ncrResult || "-"}
                </div>
                <div>
                  <span className="text-gray-500">보고자: </span>
                  {ncrReporter || "-"}
                </div>
                <div>
                  <span className="text-gray-500">조치부서: </span>
                  {ncrAssignee || "-"}
                </div>
                <div>
                  <span className="text-gray-500">발생위치/공종: </span>
                  {ncrArea || "-"}
                </div>
                <div>
                  <span className="text-gray-500">조치기한: </span>
                  {ncrDue || "-"}
                </div>
              </div>
              <div className="mt-2">
                <div className="text-gray-500">부적합 내용</div>
                <div className="whitespace-pre-wrap break-words">
                  {ncrCause || "-"}
                </div>
              </div>
              <div className="mt-2">
                <div className="text-gray-500">조치(기존 NCR 입력)</div>
                <div className="whitespace-pre-wrap break-words">
                  {ncrAction || "-"}
                </div>
              </div>
            </div>
          )}
        </section>

        {/* 기본 입력 (TableCell 적용) */}
        <section className="mt-6">
          <table className="w-full border border-gray-300">
            <tbody>
              {/* 문서일자 */}
              <tr>
                <TableCell
                  label="문서일자"
                  editing
                  value={form.doc_date}
                  input={
                    <input
                      type="date"
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={form.doc_date}
                      onChange={(e) => onChange("doc_date", e.target.value)}
                    />
                  }
                />

                {/* 상태 */}
                <TableCell
                  label="상태"
                  editing
                  value={form.status}
                  input={
                    <select
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={form.status}
                      onChange={(e) => onChange("status", e.target.value)}
                    >
                      <option value="draft">작성중</option>
                      <option value="in_progress">조치중</option>
                      <option value="pending_verify">검증대기</option>
                      <option value="closed">종결</option>
                    </select>
                  }
                />
              </tr>

              {/* 제목 */}
              <tr>
                <TableCell
                  label="제목"
                  editing
                  colSpan={3}
                  value={form.title}
                  input={
                    <>
                      <input
                        className="mt-1 w-full rounded border px-3 py-2 text-sm"
                        placeholder={autoTitle || "문서 제목"}
                        value={form.title}
                        onChange={(e) => onChange("title", e.target.value)}
                      />
                      {autoTitle && (
                        <p className="mt-1 text-xs text-gray-500">
                          미입력 시 자동 제목: {autoTitle}
                        </p>
                      )}
                    </>
                  }
                />
              </tr>

              {/* 비고 */}
              <tr>
                <TableCell
                  label="비고"
                  editing
                  colSpan={3}
                  value={form.note}
                  input={
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      rows={3}
                      value={form.note}
                      onChange={(e) => onChange("note", e.target.value)}
                      placeholder="추가 메모가 있을 경우 작성하세요."
                    />
                  }
                />
              </tr>
            </tbody>
          </table>
        </section>

        {/* CAR 세부 (TableCell 적용) */}
        <section className="mt-6">
          <table className="w-full border border-gray-300">
            <colgroup>
              <col style={{ width: "150px" }} />
              <col />
              <col style={{ width: "150px" }} />
              <col />
            </colgroup>
            <tbody>
              {/* 연결 NCR */}
              <tr>
                <TableCell
                  label="연결 NCR (자동)"
                  editing={false}
                  colSpan={3}
                  value={carMeta.ncr_dns || linkedNcr?.doc_dns || ""}
                />
              </tr>

              {/* 책임부서 */}
              <tr>
                {/* <TableCell
          label="책임부서/담당"
          editing
          value={carMeta.responsible}
          input={
            <input
              className="mt-1 w-full rounded border px-3 py-2 text-sm"
              value={carMeta.responsible}
              onChange={(e) => onChangeMeta("responsible", e.target.value)}
              placeholder="예시) 건축품질팀 / 홍길동"
            />
          }
        /> */}
                <TableCell
                  label="조치 담당자"
                  colSpan={3}
                  editing
                  input={
                    <PeopleCellLoader
                      dns={dns}
                      value={requestPeople}
                      onChange={(list) => setRequestPeople(list)}
                    />
                  }
                />


              </tr>
              <tr>
                {/* 조치기한 */}
                <TableCell
                  label="조치기한"
                  editing
                  value={carMeta.due_date}
                  input={
                    <input
                      type="date"
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={carMeta.due_date}
                      onChange={(e) => onChangeMeta("due_date", e.target.value)}
                    />
                  }
                />
                <TableCell
                  label="종결일"
                  editing
                  value={carMeta.close_date}
                  input={
                    <input
                      type="date"
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={carMeta.close_date}
                      onChange={(e) => onChangeMeta("close_date", e.target.value)}
                    />
                  }
                />
              </tr>
              <tr>
                {/* 위험도 */}
                <TableCell
                  label="위험도 (Severity)"
                  editing
                  colSpan={3}
                  value={carMeta.severity}
                  input={
                    <select
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      value={carMeta.severity}
                      onChange={(e) => onChangeMeta("severity", e.target.value)}
                    >
                      <option value="">선택</option>
                      <option value="High">High</option>
                      <option value="Medium">Medium</option>
                      <option value="Low">Low</option>
                    </select>
                  }
                />
              </tr>

              {/* 근본원인 */}
              <tr>
                <TableCell
                  label="근본원인 (Root Cause)"
                  editing
                  colSpan={3}
                  rich={false}
                  value={carMeta.root_cause}
                  input={
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      rows={3}
                      value={carMeta.root_cause}
                      onChange={(e) => onChangeMeta("root_cause", e.target.value)}
                    />
                  }
                />
              </tr>

              {/* 시정조치 */}
              <tr>
                <TableCell
                  label="시정조치 (Corrective Action)"
                  editing
                  colSpan={3}
                  rich={false}
                  value={carMeta.corrective_action}
                  input={
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      rows={3}
                      value={carMeta.corrective_action}
                      onChange={(e) =>
                        onChangeMeta("corrective_action", e.target.value)
                      }
                    />
                  }
                />
              </tr>

              {/* 예방조치 */}
              <tr>
                <TableCell
                  label="예방조치 (Preventive Action)"
                  editing
                  colSpan={3}
                  rich={false}
                  value={carMeta.preventive_action}
                  input={
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      rows={3}
                      value={carMeta.preventive_action}
                      onChange={(e) =>
                        onChangeMeta("preventive_action", e.target.value)
                      }
                    />
                  }
                />
              </tr>

              {/* 검증 */}
              <tr>
                <TableCell
                  label="검증방법/기준"
                  editing
                  colSpan={3}
                  value={carMeta.verification}
                  input={
                    <textarea
                      className="mt-1 w-full rounded border px-3 py-2 text-sm"
                      rows={2}
                      value={carMeta.verification}
                      onChange={(e) => onChangeMeta("verification", e.target.value)}
                    />
                  }
                />
              </tr>
            </tbody>
          </table>
        </section>

      </div>
    </FormContentManager>
  );
}
